import { useEffect, useState, useCallback, useRef } from 'react';
import { hasUnsavedWork } from '@/hooks/useBeforeUnload';

const SW_URL = '/sw-custom.js';
const STARTUP_GRACE_PERIOD_MS = 5000; // nos primeiros 5s ninguém digitou nada: aplica direto
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;

// Atualização de versão (I.150): a aba sem trabalho aberto atualiza sozinha;
// a aba com alteração não salva mostra o aviso e só atualiza pelo botão.
// O registro é feito aqui, e não pela biblioteca do PWA, porque a biblioteca
// recarrega TODA aba quando a versão nova assume, inclusive a que tem
// formulário sujo (pergunta "sair do site?" e, se cancelada, aba presa).
export function useServiceWorkerUpdate() {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const mountTimeRef = useRef<number>(Date.now());
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const userRequestedRef = useRef(false);
  const reloadingRef = useRef(false);

  const reloadOnce = useCallback(() => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    window.location.reload();
    // Se a pessoa cancelou a pergunta do navegador, o botão volta a valer
    window.setTimeout(() => { reloadingRef.current = false; }, 3000);
  }, []);

  const canApplySilently = useCallback(() => {
    const inGracePeriod = Date.now() - mountTimeRef.current < STARTUP_GRACE_PERIOD_MS;
    return inGracePeriod || !hasUnsavedWork();
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

    // Já existe uma versão ativa (a aba nasceu controlada, ou o SW já estava ativo
    // quando o registro resolveu, caso da recarga forçada): a partir daí, troca de
    // controlador = versão nova. Na primeira visita, a primeira troca é só a
    // instalação inicial assumindo a aba.
    let hasActiveVersion = Boolean(navigator.serviceWorker.controller);
    let intervalId: number | undefined;

    const onNewVersionWaiting = () => {
      const registration = registrationRef.current;
      const waiting = registration?.waiting;
      if (!waiting || !registration?.active) return;
      console.log('[SW] New version waiting');
      if (canApplySilently()) {
        waiting.postMessage({ type: 'SKIP_WAITING' });
      } else {
        console.log('[SW] Unsaved work in this tab, showing update banner');
        setNeedsRefresh(true);
      }
    };

    // A versão nova assumiu (por esta aba ou por outra)
    const onControllerChange = () => {
      if (!hasActiveVersion) {
        hasActiveVersion = true;
        return;
      }
      if (userRequestedRef.current || canApplySilently()) {
        // Se a recarga for cancelada na pergunta do navegador, a faixa fica como saída
        setNeedsRefresh(true);
        reloadOnce();
      } else {
        console.log('[SW] New version active, keeping unsaved work and showing banner');
        setNeedsRefresh(true);
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .then((registration) => {
        console.log('[SW] Service Worker registered:', SW_URL);
        registrationRef.current = registration;
        if (registration.active) hasActiveVersion = true;

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') onNewVersionWaiting();
            if (newWorker.state === 'redundant') {
              console.warn('[SW] SW update failed (redundant) — running without update');
            }
          });
        });

        // Versão nova que já estava esperando antes desta aba abrir
        onNewVersionWaiting();

        intervalId = window.setInterval(() => {
          registration.update().catch(() => {});
        }, UPDATE_CHECK_INTERVAL_MS);
        registration.update().catch(() => {});
      })
      .catch((error) => {
        console.error('[SW] Registration error:', error);
      });

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [canApplySilently, reloadOnce]);

  const updateServiceWorker = useCallback(() => {
    console.log('[SW] User triggered update');
    userRequestedRef.current = true;
    const waiting = registrationRef.current?.waiting;
    if (waiting) {
      // A recarga acontece na troca de controlador
      waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // A versão nova já assumiu (aplicada por outra aba): basta recarregar
      reloadOnce();
    }
  }, [reloadOnce]);

  return { needsRefresh, updateServiceWorker };
}
